import { useMemo } from "react";
import { CalendarEvent, EVENT_TYPE_CONFIG } from "@/hooks/useCalendarEvents";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarEventPopover } from "./CalendarEventPopover";

interface Props {
  month: Date;
  events: CalendarEvent[];
  layers: Record<string, boolean>;
  selectedDate: Date;
  onDayClick: (d: Date) => void;
}

function getMonthGrid(month: Date) {
  const year = month.getFullYear();
  const m = month.getMonth();
  const first = new Date(year, m, 1);
  const last = new Date(year, m + 1, 0);
  const startPad = first.getDay();
  const totalDays = last.getDate();

  const weeks: (Date | null)[][] = [];
  let week: (Date | null)[] = [];
  for (let i = 0; i < startPad; i++) week.push(null);
  for (let d = 1; d <= totalDays; d++) {
    week.push(new Date(year, m, d));
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }
  if (week.length > 0) {
    while (week.length < 7) week.push(null);
    weeks.push(week);
  }
  return weeks;
}

function dateKey(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export const CalendarMonthView = ({ month, events, layers, selectedDate, onDayClick }: Props) => {
  const weeks = useMemo(() => getMonthGrid(month), [month]);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const eventsByDate = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};
    events
      .filter((e) => layers[e.event_type] !== false)
      .forEach((e) => {
        const start = new Date(e.start_date + "T00:00:00");
        const end = e.end_date ? new Date(e.end_date + "T00:00:00") : start;
        const cur = new Date(start);
        while (cur <= end) {
          const k = dateKey(cur);
          if (!map[k]) map[k] = [];
          map[k].push(e);
          cur.setDate(cur.getDate() + 1);
        }
      });
    return map;
  }, [events, layers]);

  const MAX_PILLS = 3;

  return (
    <div className="flex-1 overflow-auto">
      {/* Day headers */}
      <div className="grid grid-cols-7 border-b">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="px-2 py-1.5 text-[10px] font-semibold text-muted-foreground text-center uppercase">
            {d}
          </div>
        ))}
      </div>
      {/* Weeks */}
      {weeks.map((week, wi) => (
        <div key={wi} className="grid grid-cols-7 border-b min-h-[100px]">
          {week.map((day, di) => {
            if (!day) {
              return <div key={`empty-${di}`} className="border-r bg-muted/30" />;
            }
            const k = dateKey(day);
            const dayEvents = eventsByDate[k] || [];
            const isToday =
              day.getFullYear() === today.getFullYear() &&
              day.getMonth() === today.getMonth() &&
              day.getDate() === today.getDate();
            const isWeekend = di === 0 || di === 6;

            return (
              <div
                key={k}
                className={cn(
                  "border-r p-1 cursor-pointer transition-colors hover:bg-muted/40",
                  isWeekend && "bg-muted/20"
                )}
                onClick={() => onDayClick(day)}
              >
                <div className="flex justify-end">
                  <span
                    className={cn(
                      "text-xs h-5 w-5 flex items-center justify-center rounded-full",
                      isToday && "bg-primary text-primary-foreground font-bold"
                    )}
                  >
                    {day.getDate()}
                  </span>
                </div>
                <div className="mt-0.5 space-y-0.5">
                  {dayEvents.slice(0, MAX_PILLS).map((ev) => {
                    const cfg = EVENT_TYPE_CONFIG[ev.event_type];
                    return (
                      <Popover key={ev.id}>
                        <PopoverTrigger asChild>
                          <button
                            onClick={(e) => e.stopPropagation()}
                            className={cn(
                              "w-full text-left text-[10px] leading-tight px-1 py-0.5 rounded truncate border-l-2",
                              cfg.bgClass,
                              cfg.textClass,
                              cfg.borderClass
                            )}
                          >
                            {ev.title}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72 p-0" align="start">
                          <CalendarEventPopover event={ev} />
                        </PopoverContent>
                      </Popover>
                    );
                  })}
                  {dayEvents.length > MAX_PILLS && (
                    <span className="text-[9px] text-muted-foreground pl-1">
                      +{dayEvents.length - MAX_PILLS} more
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};
